import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetUnit,
  AssetUnitAddResponse,
  AssetUnitListResponse,
  AssetUnitRowReason,
} from '@siliconx/shared/dto/asset-unit';
import { ASSET_UNIT_ROW_REASON_MESSAGES, UNIT_SERIAL_MAX_BATCH, validateUnitSerial } from '@siliconx/shared/dto/asset-unit';
import { authFetch, API_BASE } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { SERIAL_MASK } from './MaskedSerial';
import { UnitCameraCapture } from './UnitCameraCapture';
import type { CaptureSave } from './UnitCameraCapture';

/**
 * SIL-1916: "Units" section of a stock line (quantity > 1). Shows
 * `N of quantity` serials entered, a single-entry field and a bulk paste (one
 * serial per line) that reports each rejected line while valid lines still
 * save. Writes are org-admin only (the server enforces it; the controls are
 * hidden for members). Serials are masked: the list carries none, and each
 * one is revealed on demand through the audited per-unit route (SIL-1269).
 * The revealed value lives in component state only.
 */

const REQUEST_ERROR_MESSAGES: Record<string, string> = {
  admin_only: 'Only an organization admin can change serials.',
  asset_not_found: 'This asset could not be found.',
  unit_not_found: 'That unit no longer exists. The list was refreshed.',
  asset_sold: 'This line has been sold and is locked.',
  not_a_line: 'Serials by unit apply to multi-unit lines only.',
  too_many_serials: `Add at most ${UNIT_SERIAL_MAX_BATCH} serials at a time.`,
  payload_too_large: 'That list is too large.',
  rate_limited: 'Too many reveals. Try again in a few minutes.',
  busy: 'Another change to this line is in progress. Try again.',
};
const GENERIC_ERROR = 'Something went wrong. Try again.';

class UnitsError extends Error {
  constructor(public readonly code: string | null) {
    super(code ?? 'units_failed');
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await authFetch(`${API_BASE}/api/client/assets/${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: { code?: string } | string };
    const code = typeof json.error === 'string' ? json.error : (json.error?.code ?? null);
    throw new UnitsError(code);
  }
  return (await res.json()) as T;
}

/** Non-empty pasted lines, each with its 1-based position in the textarea. */
export function parseBulkLines(text: string): { line: number; text: string }[] {
  return text
    .split(/\r?\n/)
    .map((raw, i) => ({ line: i + 1, text: raw.trim() }))
    .filter((l) => l.text !== '');
}

export interface BulkRejection {
  line: number;
  reason: AssetUnitRowReason;
}

/** Map per-index results back to the pasted line numbers. */
export function summarizeBulk(
  entries: { line: number; text: string }[],
  response: AssetUnitAddResponse,
): { saved: number; rejected: BulkRejection[] } {
  const rejected: BulkRejection[] = [];
  for (const r of response.results) {
    const entry = entries[r.index - 1];
    if (r.status === 'rejected' && entry && r.reason) rejected.push({ line: entry.line, reason: r.reason });
  }
  return { saved: response.accepted, rejected };
}

function UnitRow({
  assetId,
  unit,
  ordinal,
  canEdit,
  locked,
  onChanged,
}: {
  assetId: string;
  unit: AssetUnit;
  ordinal: number;
  canEdit: boolean;
  locked: boolean;
  onChanged: () => void;
}) {
  const [serial, setSerial] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const base = `${encodeURIComponent(assetId)}/units/${encodeURIComponent(unit.id)}`;

  async function reveal() {
    setBusy(true);
    setError(null);
    try {
      const r = await request<{ serial: string }>('GET', `${base}/serial`);
      setSerial(r.serial);
    } catch (err) {
      const code = err instanceof UnitsError ? err.code : null;
      setError((code && REQUEST_ERROR_MESSAGES[code]) || 'Could not reveal the serial.');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const reason = validateUnitSerial(draft);
    if (reason) {
      setError(ASSET_UNIT_ROW_REASON_MESSAGES[reason]);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await request('PUT', base, { serial: draft.trim(), source: 'manual' });
      setEditing(false);
      setSerial(null);
      onChanged();
    } catch (err) {
      const code = err instanceof UnitsError ? err.code : null;
      setError(
        (code && (ASSET_UNIT_ROW_REASON_MESSAGES as Record<string, string>)[code]) ||
          (code && REQUEST_ERROR_MESSAGES[code]) ||
          GENERIC_ERROR,
      );
      if (code === 'unit_not_found') onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await request('DELETE', base);
      onChanged();
    } catch (err) {
      const code = err instanceof UnitsError ? err.code : null;
      setError((code && REQUEST_ERROR_MESSAGES[code]) || GENERIC_ERROR);
      if (code === 'unit_not_found') onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm" data-testid="unit-row">
      <span className="w-8 text-xs text-muted-foreground">#{ordinal}</span>
      {editing ? (
        <>
          <input
            aria-label={`New serial for unit ${ordinal}`}
            className="w-56 rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoComplete="off"
          />
          <button type="button" className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50" disabled={busy} onClick={() => void save()}>
            Save
          </button>
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => { setEditing(false); setError(null); }}>
            Cancel
          </button>
        </>
      ) : serial !== null ? (
        <>
          <span className="font-mono text-xs" data-testid="unit-serial-revealed">{serial}</span>
          <button type="button" className="text-xs font-medium text-blue-600 hover:text-blue-800" onClick={() => setSerial(null)}>
            Hide
          </button>
        </>
      ) : (
        <>
          <span className="font-mono text-xs" aria-label="Serial number hidden" data-testid="unit-serial-masked">{SERIAL_MASK}</span>
          <button type="button" className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50" disabled={busy} onClick={() => void reveal()}>
            Reveal
          </button>
        </>
      )}
      <span className="text-xs text-muted-foreground">
        {unit.source === 'camera' ? 'camera' : 'manual'} - {new Date(unit.capturedAt).toLocaleDateString()}
      </span>
      {canEdit && !locked && !editing && (
        <span className="ml-auto flex gap-3">
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => { setDraft(''); setEditing(true); setError(null); }}>
            Edit
          </button>
          <button type="button" className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50" disabled={busy} onClick={() => void remove()}>
            Remove
          </button>
        </span>
      )}
      {error && (
        <span role="alert" className="w-full pl-11 text-xs text-red-600">
          {error}
        </span>
      )}
    </li>
  );
}

export function UnitsCard({
  assetId,
  quantity,
  canEdit,
}: {
  assetId: string;
  quantity: number;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = qk.assets.units(assetId);
  const [single, setSingle] = useState('');
  const [bulk, setBulk] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<{ saved: number; rejected: BulkRejection[] } | null>(null);

  const list = useQuery({
    queryKey,
    queryFn: () => request<AssetUnitListResponse>('GET', `${encodeURIComponent(assetId)}/units`),
    enabled: quantity > 1,
  });

  const add = useMutation({
    mutationFn: ({ serials, source }: { serials: string[]; source: 'manual' | 'camera' }) =>
      request<AssetUnitAddResponse>('POST', `${encodeURIComponent(assetId)}/units`, { serials, source }),
  });
  const [capturing, setCapturing] = useState(false);

  // SIL-1917: camera capture saves through the same add API, source 'camera'.
  const saveCaptured: CaptureSave = async (serial, source) => {
    try {
      const response = await add.mutateAsync({ serials: [serial], source });
      const row = response.results[0];
      await refresh();
      if (row && row.status === 'rejected') return { ok: false, reason: row.reason ?? 'error' };
      return { ok: true };
    } catch {
      return { ok: false, reason: 'error' };
    }
  };

  if (quantity <= 1) return null;

  const units = list.data?.units ?? [];
  const locked = list.data?.locked ?? false;
  const captured = list.data?.summary.unitsCaptured ?? 0;
  const free = Math.max(0, quantity - captured);
  const bulkEntries = parseBulkLines(bulk);
  const overCapacity = bulkEntries.length > free;
  const tooMany = bulkEntries.length > UNIT_SERIAL_MAX_BATCH;

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) }),
    ]);
  }

  async function submit(entries: { line: number; text: string }[], onDone: (r: { saved: number; rejected: BulkRejection[] }) => void) {
    setMessage(null);
    try {
      const response = await add.mutateAsync({ serials: entries.map((e) => e.text), source: 'manual' });
      onDone(summarizeBulk(entries, response));
      await refresh();
    } catch (err) {
      const code = err instanceof UnitsError ? err.code : null;
      setMessage((code && REQUEST_ERROR_MESSAGES[code]) || GENERIC_ERROR);
    }
  }

  function submitSingle(e: React.FormEvent) {
    e.preventDefault();
    const reason = validateUnitSerial(single);
    if (reason) {
      setMessage(ASSET_UNIT_ROW_REASON_MESSAGES[reason]);
      return;
    }
    void submit([{ line: 1, text: single.trim() }], (r) => {
      if (r.rejected.length > 0) setMessage(ASSET_UNIT_ROW_REASON_MESSAGES[r.rejected[0]!.reason]);
      else setSingle('');
    });
  }

  function submitBulk(e: React.FormEvent) {
    e.preventDefault();
    if (bulkEntries.length === 0 || overCapacity || tooMany) return;
    setResult(null);
    void submit(bulkEntries, (r) => {
      setResult(r);
      // Keep only the rejected lines so the user can fix and resubmit them.
      const rejectedLines = new Set(r.rejected.map((x) => x.line));
      setBulk(
        bulk
          .split(/\r?\n/)
          .filter((_, i) => rejectedLines.has(i + 1))
          .join('\n'),
      );
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3" data-testid="units-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Units</p>
        {list.data ? (
          <p className="text-sm font-semibold text-foreground" data-testid="units-progress">
            {captured.toLocaleString()} of {quantity.toLocaleString()} serials entered
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Loading units...</p>
        )}
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, (captured / quantity) * 100)}%` }} />
      </div>

      {list.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          Could not load units.
        </p>
      )}

      {units.length > 0 && (
        <ul className="mt-3 divide-y divide-border">
          {units.map((u, i) => (
            <UnitRow key={u.id} assetId={assetId} unit={u} ordinal={i + 1} canEdit={canEdit} locked={locked} onChanged={() => void refresh()} />
          ))}
        </ul>
      )}

      {canEdit && !locked && (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <button
            type="button"
            disabled={free === 0}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
            onClick={() => setCapturing(true)}
            data-testid="units-scan-open"
          >
            Scan with camera
          </button>
          <form onSubmit={submitSingle} className="flex flex-wrap items-center gap-2">
            <input
              aria-label="Serial number"
              className="w-64 rounded-md border border-border bg-background px-2 py-1.5 font-mono text-sm"
              placeholder="Serial / service tag"
              value={single}
              onChange={(e) => setSingle(e.target.value)}
              disabled={free === 0 || add.isPending}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={free === 0 || add.isPending || single.trim() === ''}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              Add
            </button>
          </form>

          <form onSubmit={submitBulk} className="space-y-2">
            <label className="block text-xs font-medium text-muted-foreground" htmlFor="units-bulk">
              Paste serials, one per line
            </label>
            <textarea
              id="units-bulk"
              rows={4}
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
              value={bulk}
              onChange={(e) => {
                setBulk(e.target.value);
                setResult(null);
              }}
              disabled={free === 0 || add.isPending}
              autoComplete="off"
              spellCheck={false}
            />
            {overCapacity && (
              <p role="alert" className="text-xs text-red-600" data-testid="units-over-capacity">
                {bulkEntries.length} lines pasted but only {free} {free === 1 ? 'unit is' : 'units are'} free.
              </p>
            )}
            {tooMany && (
              <p role="alert" className="text-xs text-red-600">
                {REQUEST_ERROR_MESSAGES.too_many_serials}
              </p>
            )}
            <button
              type="submit"
              disabled={bulkEntries.length === 0 || overCapacity || tooMany || add.isPending}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              {add.isPending ? 'Saving...' : 'Add pasted serials'}
            </button>
          </form>

          {message && (
            <p role="alert" className="text-sm text-red-600" data-testid="units-message">
              {message}
            </p>
          )}

          {result && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="units-result">
              <p className="font-medium text-foreground">
                {result.saved} saved{result.rejected.length > 0 ? `, ${result.rejected.length} rejected` : ''}
              </p>
              {result.rejected.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-red-700">
                  {result.rejected.map((r) => (
                    <li key={r.line} data-testid="units-rejected-line">
                      Line {r.line}: {ASSET_UNIT_ROW_REASON_MESSAGES[r.reason]}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {capturing && (
        <UnitCameraCapture captured={captured} quantity={quantity} onSave={saveCaptured} onClose={() => setCapturing(false)} />
      )}
    </div>
  );
}
